import {NullAsDefault} from "views/Shared";
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {createAsyncAction} from '.'
import {getAsynCacheSelector} from './selectors'

function useAsyncAction({
                            query = {},
                            values = {},
                            apiInfo,
                            onSuccess = NullAsDefault,
                            onError = NullAsDefault
                        }) {
    const mounted = useRef(true)
    useEffect(() => {
        return () => { mounted.current = false }
    }, [])

    const [action, setAction] = useState()

    const {
        isLoading,
        success,
        errorMessages,
        result,
        time,
        error,
        response,
    } = useSelector(state => getAsynCacheSelector(state, action?.asyncId))

    const dispatch = useDispatch()

    // Stabilize query/values/apiInfo bằng ref ngay trong hook
    // → caller không cần lo object identity, truyền inline object vẫn hoạt động đúng
    // → handleAsyncAction luôn đọc giá trị mới nhất qua ref, không bị stale closure
    const queryRef = useRef(query)
    const valuesRef = useRef(values)
    const apiInfoRef = useRef(apiInfo)
    useEffect(() => { queryRef.current = query }, [query])
    useEffect(() => { valuesRef.current = values }, [values])
    useEffect(() => { apiInfoRef.current = apiInfo }, [apiInfo])

    const onSuccessRef = useRef(onSuccess)
    const onErrorRef = useRef(onError)
    useEffect(() => { onSuccessRef.current = onSuccess }, [onSuccess])
    useEffect(() => { onErrorRef.current = onError }, [onError])

    // deps [] — stable mãi mãi, đọc mọi thứ qua ref
    const handleAsyncAction = useCallback((_values = {}, _query = {}) => {
        const newAction = createAsyncAction({
            query: {...queryRef.current, ..._query},
            apiInfo: apiInfoRef.current,
            values: {...valuesRef.current, ..._values}
        })
        setAction(newAction)
        dispatch(newAction)
        return newAction
    }, [dispatch])  // chỉ dispatch là cần — stable từ redux

    useEffect(() => {
        if (!action) return
        const timeout = isLoading ? 300 : 50
        const timer = setTimeout(() => {
            if (!mounted.current) return
            if (error) {
                console.error(errorMessages)
                onErrorRef.current(error, errorMessages)
            } else if (success) {
                onSuccessRef.current(result, {
                    action, isLoading, success, errorMessages, result, error, time,
                    response, handleAsyncAction,
                })
            }
        }, timeout)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, action, error, success])

    return {
        action,
        isLoading,
        success,
        errorMessages,
        result,
        error,
        time,
        response,
        handleAsyncAction,
    }
}

export default useAsyncAction